import { useGetExpenseRegularizationEligibilityQuery } from "src/graphql-next/generated/queries";
import { FeatureFlagNamesEnum } from "src/graphql/generated/types";
import { decodeBase64UUID } from "src/utils";

import { useCurrentAccount } from "./useCurrentAccount";
import { useHasFeatureFlagActivated } from "./useHasFeatureFlagActivated";
import { useUuid } from "./useUuid";

type ExpenseRegularizationEligibility = {
  isEligible: boolean;
  eligibleBuildingIds: Array<string>;
  hasExternalTrusteeBuildings: boolean;
  isBannerEligible: boolean;
};

/*
Landlord-only eligibility for the "Documents de ma copropriété" category (external expense
regularization / RCL). The category itself is shown to every landlord with at least one
external trustee building (`hasExternalTrusteeBuildings`), while the upload form stays
gated by the feature flag (`isEligible`).
*/
export const useExpenseRegularizationEligibility =
  (): ExpenseRegularizationEligibility => {
    const uuid = useUuid();
    const currentAccount = useCurrentAccount();
    const isLandlord = currentAccount.__typename === "LandlordAccount";

    const isExpenseRegularizationUploadEnabled = useHasFeatureFlagActivated(
      FeatureFlagNamesEnum.CustomerPortalUploadExternalExpenseRegularization,
    );

    const { accountId } = decodeBase64UUID(uuid);

    const { data } = useGetExpenseRegularizationEligibilityQuery(
      { accountId },
      { enabled: isLandlord },
    );

    const account = data?.account;
    const eligibility =
      account?.__typename === "LessorAccount"
        ? account.externalExpenseRegularization
        : undefined;

    return {
      isEligible:
        isExpenseRegularizationUploadEnabled &&
        (eligibility?.isEligible ?? false),
      eligibleBuildingIds: eligibility?.eligibleBuildingIds ?? [],
      hasExternalTrusteeBuildings:
        eligibility?.hasExternalTrusteeBuildings ?? false,
      isBannerEligible:
        isExpenseRegularizationUploadEnabled &&
        (eligibility?.isBannerEligible ?? false),
    };
  };
